import { Hr } from '@euler/app/components/Hr';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { RelatedFaultLikeSubject } from '@euler/model/task-detail/RelatedFaultLikeSubject';
import { memo, useCallback } from 'react';
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

export const kMaintenanceAdviceFallbackText = '暂无';

export const RelatedFaultLikeSubjectsToSolve = memo(
  ({
    title,
    subjects,
    style,
    labelStyle,
    onToggle,
  }: {
    style?: StyleProp<ViewStyle>;
    title?: string;
    labelStyle?: StyleProp<TextStyle>;
    subjects: RelatedFaultLikeSubject[];
    onToggle?: (name: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      inspection: {
        siteInspection: {
          itemCard: {
            maintenanceAdviceLabelColor: labelColor,
            relatedFaultLikeSubject: { borderColor },
          },
        },
      },
    } = useTheme();

    const handleToggle = useCallback(
      (name: string) => {
        onToggle?.(name);
      },
      [onToggle],
    );

    if (!subjects.length) return null;

    return (
      <View style={style}>
        <Typography.Label
          light
          size={rpx(14)}
          color={labelColor}
          mb={rpx(10)}
          style={labelStyle}
        >
          {title ?? '相关故障检修:'}
        </Typography.Label>
        <StackLayout
          divider={<Hr color={borderColor} />}
          spacing={0}
          style={{
            borderColor,
            borderWidth: StyleSheet.hairlineWidth,
            borderRadius: rpx(10),
            backgroundColor: 'white',
          }}
        >
          {subjects.map(subject => (
            <Cell
              name={subject.name}
              checked={subject.checked}
              key={subject.name}
              onToggle={handleToggle}
            />
          ))}
        </StackLayout>
      </View>
    );
  },
);

const Cell = memo(
  ({
    name,
    checked,
    style,
    onToggle,
  }: {
    name: string;
    checked?: boolean;
    style?: StyleProp<ViewStyle>;
    onToggle?: (name: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      inspection: {
        siteInspection: {
          itemCard: {
            relatedFaultLikeSubject: { textColor, activeTextColor },
          },
        },
      },
    } = useTheme();

    const handlePress = useCallback(() => {
      onToggle?.(name);
    }, [name, onToggle]);

    return (
      <TouchableOpacity
        style={style}
        onPress={handlePress}
        css={`
          padding: ${rpx(8)}px ${rpx(12)}px;
        `}
      >
        <StackLayout direction="row" alignItems="center" spacing={rpx(4)}>
          <Icon
            name={checked ? 'checkmark-circle' : 'radio_button_off'}
            size={rpx(16)}
            color={checked ? activeTextColor : textColor}
          />
          <Typography.Text
            size={rpx(14)}
            lineHeight={rpx(20)}
            color={checked ? activeTextColor : textColor}
          >
            {name}
          </Typography.Text>
        </StackLayout>
      </TouchableOpacity>
    );
  },
);
